import { IDIOM_DATA } from "@/lib/tool-kit/word-knowledge/idioms-data";

/**
 * The Idiom Finder: a searchable list of common English idioms, grouped by theme.
 *
 * The idioms, meanings and example sentences are written for CitationEasy (see
 * `idioms-data.ts`). Origins are deliberately left out: most idiom etymologies passed
 * around online are folk stories, and a wrong one is worse than none.
 */

export type IdiomTheme =
  | "time"
  | "money"
  | "work"
  | "success"
  | "feelings"
  | "talk"
  | "effort"
  | "risk"
  | "people"
  | "truth"
  | "thinking"
  | "nature";

export const IDIOM_THEMES: readonly IdiomTheme[] = [
  "time",
  "money",
  "work",
  "success",
  "feelings",
  "talk",
  "effort",
  "risk",
  "people",
  "truth",
  "thinking",
  "nature",
];

const THEME_CODES: Readonly<Record<string, IdiomTheme>> = {
  t: "time",
  m: "money",
  w: "work",
  s: "success",
  f: "feelings",
  k: "talk",
  e: "effort",
  r: "risk",
  p: "people",
  h: "truth",
  n: "thinking",
  a: "nature",
};

export interface Idiom {
  id: number;
  text: string;
  themes: IdiomTheme[];
  /** Informal or spoken: avoid in formal academic prose. */
  informal: boolean;
  meaning: string;
  example: string;
  variants: string[];
}

const RE_LINE_BREAK = /\n/;
const RE_SEMICOLON = /\s*;\s*/;
const RE_TOKEN_SPLIT = /[^\p{L}\p{N}']+/u;
const RE_ARTICLE = /^(?:a|an|the)\s+/;

function parseTheme(code: string): IdiomTheme {
  const theme = THEME_CODES[code];
  if (!theme) {
    throw new Error(`Unknown idiom theme code "${code}"`);
  }
  return theme;
}

/** The compact text → idioms. Throws on a malformed line, so a bad edit fails a test. */
export function parseIdioms(text: string): Idiom[] {
  const idioms: Idiom[] = [];
  for (const line of text.split(RE_LINE_BREAK)) {
    if (!line.trim()) {
      continue;
    }
    const [idiom, themes, register, meaning, example, also] = line.split("|");
    if (!(idiom && themes && register && meaning && example)) {
      throw new Error(`Malformed idiom entry: ${line}`);
    }
    idioms.push({
      id: idioms.length,
      text: idiom,
      themes: [...themes].map(parseTheme),
      informal: register === "i",
      meaning,
      example,
      variants: also ? also.split(RE_SEMICOLON) : [],
    });
  }
  return idioms;
}

export const IDIOMS: readonly Idiom[] = parseIdioms(IDIOM_DATA);

export type RegisterFilter = "all" | "neutral" | "informal";

export interface IdiomSearch {
  query: string;
  theme: "all" | IdiomTheme;
  register: RegisterFilter;
}

/** Sort key: lower case, ignoring a leading "a", "an" or "the". */
function sortKey(idiom: Idiom): string {
  return idiom.text.toLowerCase().replace(RE_ARTICLE, "");
}

function tokensOf(query: string): string[] {
  const tokens = query
    .toLowerCase()
    .split(RE_TOKEN_SPLIT)
    .filter((token) => token.length > 0);
  const meaningful = tokens.filter((token) => token.length > 2);
  return meaningful.length > 0 ? meaningful : tokens;
}

function hasWord(haystack: string, token: string): boolean {
  return haystack.split(RE_TOKEN_SPLIT).includes(token);
}

const IDIOM_WORD = 100;
const IDIOM_PART = 70;
const MEANING_WORD = 50;
const EXAMPLE_WORD = 25;
const PHRASE_BONUS = 200;

function tokenScore(idiom: Idiom, token: string): number {
  const forms = [idiom.text, ...idiom.variants].map((form) =>
    form.toLowerCase()
  );
  if (forms.some((form) => hasWord(form, token))) {
    return IDIOM_WORD;
  }
  if (token.length >= 3 && forms.some((form) => form.includes(token))) {
    return IDIOM_PART;
  }
  const meaning = idiom.meaning.toLowerCase();
  if (
    hasWord(meaning, token) ||
    (token.length >= 4 && meaning.includes(token))
  ) {
    return MEANING_WORD;
  }
  const example = idiom.example.toLowerCase();
  if (
    hasWord(example, token) ||
    (token.length >= 4 && example.includes(token))
  ) {
    return EXAMPLE_WORD;
  }
  return 0;
}

function passesFilters(idiom: Idiom, search: IdiomSearch): boolean {
  if (search.theme !== "all" && !idiom.themes.includes(search.theme)) {
    return false;
  }
  if (search.register === "informal") {
    return idiom.informal;
  }
  return search.register === "neutral" ? !idiom.informal : true;
}

/**
 * Idioms matching the filters and the query, best match first. Every word of the query
 * must match somewhere; a word in the idiom itself outranks one in its meaning, which
 * outranks one in its example, and the whole query appearing inside an idiom outranks
 * all of that. With no query the list is alphabetical, ignoring a leading article.
 */
export function searchIdioms(search: IdiomSearch): Idiom[] {
  const pool = IDIOMS.filter((idiom) => passesFilters(idiom, search));
  const tokens = tokensOf(search.query);
  const alphabetical = (a: Idiom, b: Idiom) =>
    sortKey(a).localeCompare(sortKey(b), "en");
  if (tokens.length === 0) {
    return [...pool].sort(alphabetical);
  }
  const phrase = search.query.trim().toLowerCase();
  const scored: { idiom: Idiom; score: number }[] = [];
  for (const idiom of pool) {
    let total = 0;
    let every = true;
    for (const token of tokens) {
      const score = tokenScore(idiom, token);
      if (score === 0) {
        every = false;
        break;
      }
      total += score;
    }
    if (!every) {
      continue;
    }
    if (phrase.includes(" ") && idiom.text.toLowerCase().includes(phrase)) {
      total += PHRASE_BONUS;
    }
    scored.push({ idiom, score: total });
  }
  return scored
    .sort((a, b) => b.score - a.score || alphabetical(a.idiom, b.idiom))
    .map((item) => item.idiom);
}

/** One idiom from `pool` for a random number in [0, 1). Null for an empty pool. */
export function pickIdiom(
  pool: readonly Idiom[],
  random: number
): Idiom | null {
  if (pool.length === 0) {
    return null;
  }
  return pool[Math.min(pool.length - 1, Math.floor(random * pool.length))];
}
